import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import axios from 'axios';

const AddVault: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [formData, setFormData] = useState({
    keyVaultName: '',
    azureTenantId: '',
    azureClientId: '',
    azureClientSecret: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Prepare the payload with the exact field names expected by the API
      const payload = {
        KeyVault_Name: String(formData.keyVaultName || '').trim(),
        Azure_Tenant_Id: String(formData.azureTenantId || '').trim(),
        Azure_Client_Id: String(formData.azureClientId || '').trim(),
        Azure_Client_Secret: String(formData.azureClientSecret || '').trim()
      };

      // Validate that all required fields are non-empty
      const requiredFields = ['KeyVault_Name', 'Azure_Tenant_Id', 'Azure_Client_Id', 'Azure_Client_Secret'];
      const missingFields = requiredFields.filter(field => !payload[field as keyof typeof payload]?.trim());
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }
      
      // Log the payload for debugging
      console.log('Sending payload:', JSON.stringify(payload, null, 2));

      const response = await axios.post(
        'https://p360nextwaveemapi.azurewebsites.net/api/KeyvaultControl/addKeyVault',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            // Add authorization header if required
            'Authorization': `Bearer ${sessionStorage.getItem('login')}`,
          },
        }
      );
      
      console.log('API Response:', response);
      
      // Check if the request was successful (status code 200-299)
      if (response.status >= 200 && response.status < 300) {
        // Show success message
        alert('Vault added successfully!');
        // Redirect to vault list on success
        navigate('/vaults');
        return; // Exit the function after successful navigation
      }
    } catch (error: any) {
      console.error('Error adding vault:', error);
      // Show error message to user
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to add vault. Please try again.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
    <Button
      startIcon={<BackIcon />}
      onClick={() => navigate(-1)}
      sx={{
        mb: 2,
        backgroundColor: '#6a1b9a',
        color: 'white',
        '&:hover': {
          backgroundColor: '#4a148c', 
        }
      }}
    >
      Back to Vaults
    </Button>
      
      <Card >
        <CardContent>
        <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
          <Typography variant="h5" gutterBottom>
            Add New Vault
          </Typography>  
          
        </Box>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Key Vault Name"
              name="keyVaultName"
              value={formData.keyVaultName}
              onChange={handleChange}
              required
              variant="outlined"
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="Azure Tenant ID"
              name="azureTenantId"
              value={formData.azureTenantId}
              onChange={handleChange}
              required
              variant="outlined"
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="Azure Client ID"
              name="azureClientId"
              type={showClientSecret ? 'text' : 'password'}
              value={formData.azureClientId}
              onChange={handleChange}
              required
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowClientSecret(!showClientSecret)}
                      edge="end"
                    >
                      {showClientSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="Azure Client Secret"
              name="azureClientSecret"
              type={showClientSecret ? 'text' : 'password'}
              value={formData.azureClientSecret}
              onChange={handleChange}
              required
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowClientSecret(!showClientSecret)}
                      edge="end"
                    >
                      {showClientSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: '#f5f5f5',
              borderTop: '1px solid #ddd',
              borderRadius: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Adding...' : 'Add Vault'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddVault;
